
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoginForm } from "./LoginForm";

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export const LoginModal = ({ open, onOpenChange, onSuccess }: LoginModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSuccess = async () => {
    console.log('🔐 LoginModal onSuccess - Checking for pending booking...');
    
    // Check for pending booking FIRST
    const hasPendingBooking = sessionStorage.getItem('pendingBooking');
    
    if (hasPendingBooking) {
      console.log('📋 Pending booking detected - waiting for AuthContext to process...');
      
      // Wait for AuthContext to finish processing booking (max 10 seconds)
      const maxWaitTime = 10000;
      const startTime = Date.now();
      
      while (Date.now() - startTime < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Check if booking processing is complete
        const stillPending = sessionStorage.getItem('pendingBooking');
        const redirectPath = sessionStorage.getItem('redirect_after_login');
        const bookingError = sessionStorage.getItem('booking_error');
        
        if (!stillPending || redirectPath || bookingError) {
          console.log('✅ Booking processing complete');
          break;
        }
      }
      
      // Small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Check for pending demographics in sessionStorage
    const pendingDemographics = sessionStorage.getItem('pending_demographics');
    
    if (pendingDemographics && user) {
      try {
        const demographics = JSON.parse(pendingDemographics);
        const token = localStorage.getItem('auth_token');
        
        if (token) {
          console.log('💾 Saving pending demographics after authentication');
          
          const response = await fetch('/api/user/profile', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(demographics)
          });

          if (response.ok) {
            console.log('✅ Demographics saved successfully');
            sessionStorage.removeItem('pending_demographics');
            toast({
              title: "Profile Updated",
              description: "Your information has been saved"
            });
          } else {
            console.warn('⚠️ Failed to save demographics after login');
          }
        }
      } catch (error) {
        console.error('❌ Error saving pending demographics:', error);
      }
    }
    
    // Check for redirect path set by AuthContext
    const redirectPath = sessionStorage.getItem('redirect_after_login');
    
    if (redirectPath) {
      console.log('🚀 LoginModal: Navigating to:', redirectPath);
      sessionStorage.removeItem('redirect_after_login');
      onOpenChange(false);
      onSuccess?.();
      // Use window.location.href for full navigation (handles booking_id in URL)
      window.location.href = redirectPath;
      return;
    }
    
    console.log('🔐 LoginModal: Closing modal - no redirect path set');
    
    // Close modal
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Welcome to HealthyOwl</DialogTitle>
          <DialogDescription>
            Sign in to access your account or create a new one to get started
          </DialogDescription>
        </DialogHeader>
        <LoginForm onSuccess={handleSuccess} />
      </DialogContent>
    </Dialog>
  );
};
